/**
 * HR/Admin page: manage corporate KPI assignments — annual view.
 *
 * Company KPIs are GLOBAL: HR creates one KPI with per-quarter targets and
 * it is automatically assigned to every active employee for each available
 * quarter-period.  Individual KPIs are self-managed by employees on the
 * "KPI Saya" page.
 */

import { useState, useMemo, useEffect } from 'react' // useEffect still used for year sync
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { penilaianApi } from '../api/penilaianApi'
import type {
  AssessmentPeriod,
  CompanyKPIRow,
  CreateCompanyKPIInput,
  UpdateCompanyKPIInput,
} from '../types/penilaian.types'

// ─── Quarter column metadata ──────────────────────────────────────────────────

const QUARTERS = [
  { q: 'Q1' as const, qn: 1, targetKey: 'q1_target' as const, idKey: 'q1_kpi_id' as const, realisasiKey: 'q1_realisasi' as const, realisasiPctKey: 'q1_realisasi_pct' as const },
  { q: 'Q2' as const, qn: 2, targetKey: 'q2_target' as const, idKey: 'q2_kpi_id' as const, realisasiKey: 'q2_realisasi' as const, realisasiPctKey: 'q2_realisasi_pct' as const },
  { q: 'Q3' as const, qn: 3, targetKey: 'q3_target' as const, idKey: 'q3_kpi_id' as const, realisasiKey: 'q3_realisasi' as const, realisasiPctKey: 'q3_realisasi_pct' as const },
  { q: 'Q4' as const, qn: 4, targetKey: 'q4_target' as const, idKey: 'q4_kpi_id' as const, realisasiKey: 'q4_realisasi' as const, realisasiPctKey: 'q4_realisasi_pct' as const },
]

type YearPeriods = {
  Q1?: AssessmentPeriod
  Q2?: AssessmentPeriod
  Q3?: AssessmentPeriod
  Q4?: AssessmentPeriod
}

// ─── KPI form state ───────────────────────────────────────────────────────────

interface KPIFormState {
  name: string
  description: string
  annual_target: string
  bobot: string
  q1_target: string
  q2_target: string
  q3_target: string
  q4_target: string
}

const emptyForm = (): KPIFormState => ({
  name: '',
  description: '',
  annual_target: '',
  bobot: '',
  q1_target: '',
  q2_target: '',
  q3_target: '',
  q4_target: '',
})

const rowToForm = (row: CompanyKPIRow): KPIFormState => ({
  name: row.name,
  description: row.target_description,
  annual_target: row.annual_target ?? '',
  bobot: String(row.bobot),
  q1_target: row.q1_target ?? '',
  q2_target: row.q2_target ?? '',
  q3_target: row.q3_target ?? '',
  q4_target: row.q4_target ?? '',
})

// ─── KPI form modal ───────────────────────────────────────────────────────────

interface KPIFormModalProps {
  year: number
  yearPeriods: YearPeriods
  /** Editing existing row; null = create mode */
  editing: CompanyKPIRow | null
  onClose: () => void
}

function KPIFormModal({ year, yearPeriods, editing, onClose }: KPIFormModalProps) {
  const qc = useQueryClient()
  const [form, setForm] = useState<KPIFormState>(
    editing ? rowToForm(editing) : emptyForm()
  )

  const isEdit = editing !== null

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name.trim(),
        target_description: form.description.trim() || form.name.trim(),
        annual_target: form.annual_target.trim() || null,
        bobot: Number(form.bobot),
        q1_target: form.q1_target.trim() || null,
        q2_target: form.q2_target.trim() || null,
        q3_target: form.q3_target.trim() || null,
        q4_target: form.q4_target.trim() || null,
      }
      if (isEdit) {
        await penilaianApi.updateCompanyKPI(editing!.name, year, payload as UpdateCompanyKPIInput)
      } else {
        await penilaianApi.createCompanyKPI({ year, ...payload } as CreateCompanyKPIInput)
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company-kpis', year] })
      toast.success(isEdit ? 'KPI berhasil diperbarui' : 'KPI Perusahaan berhasil dibuat')
      onClose()
    },
    onError: (e: any) => {
      const detail = e?.detail
      const msg = Array.isArray(detail)
        ? detail.map((d: any) => d?.msg).filter(Boolean).join('; ')
        : typeof detail === 'string' ? detail : null
      toast.error(msg || e?.error?.message || 'Gagal menyimpan KPI')
    },
  })

  const set = (key: keyof KPIFormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) { toast.error('Nama KPI wajib diisi'); return }
    if (!form.bobot || Number(form.bobot) <= 0) { toast.error('Bobot harus > 0'); return }
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="font-semibold text-gray-900 text-lg">
            {isEdit ? 'Edit KPI Perusahaan' : 'Tambah KPI Perusahaan'}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            KPI ini akan {isEdit ? 'diperbarui' : 'ditambahkan'} untuk semua karyawan aktif di semua
            kuartal yang tersedia
          </p>
        </div>

        <form id="kpi-form" onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nama KPI <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={set('name')}
              placeholder="Contoh: Profit Perusahaan"
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deskripsi
            </label>
            <textarea
              value={form.description}
              onChange={set('description')}
              placeholder="Jelaskan apa yang diukur oleh KPI ini…"
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bobot (%) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min={1}
              max={100}
              step={1}
              value={form.bobot}
              onChange={set('bobot')}
              placeholder="Contoh: 30"
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Tahunan
            </label>
            <input
              type="text"
              value={form.annual_target}
              onChange={set('annual_target')}
              placeholder="Contoh: 100 Miliar IDR (opsional)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target per Kuartal
            </label>
            <div className="space-y-2">
              {QUARTERS.map(({ q, targetKey }) => {
                const hasPeriod = !!yearPeriods[q]
                return (
                  <div
                    key={q}
                    className={`flex items-center gap-3 ${!hasPeriod ? 'opacity-40' : ''}`}
                  >
                    <span className="text-xs font-bold text-gray-500 w-7">{q}</span>
                    <input
                      type="text"
                      value={form[targetKey]}
                      onChange={set(targetKey)}
                      placeholder={
                        !hasPeriod
                          ? 'Periode belum dibuat'
                          : `Target ${q}… (opsional)`
                      }
                      disabled={!hasPeriod}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button
            type="submit"
            form="kpi-form"
            disabled={mutation.isPending}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          >
            {mutation.isPending ? 'Menyimpan…' : isEdit ? 'Simpan Perubahan' : 'Buat KPI'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
          >
            Batal
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Realisasi edit modal ─────────────────────────────────────────────────────

interface RealisasiModalProps {
  row: CompanyKPIRow
  q: string
  qn: number
  year: number
  onClose: () => void
}

function RealisasiModal({ row, q, qn, year, onClose }: RealisasiModalProps) {
  const qc = useQueryClient()
  const qInfo = QUARTERS.find((qu) => qu.q === q)!
  const [realisasi, setRealisasi] = useState<string>(row[qInfo.realisasiKey] ?? '')
  const [realisasiPct, setRealisasiPct] = useState<string>(
    row[qInfo.realisasiPctKey] != null ? String(row[qInfo.realisasiPctKey]) : ''
  )

  const mutation = useMutation({
    mutationFn: () =>
      penilaianApi.updateCompanyKPIRealisasi({
        name: row.name,
        year,
        quarter: qn,
        realisasi: realisasi.trim(),
        realisasi_pct: realisasiPct ? parseFloat(realisasiPct) : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company-kpis', year] })
      toast.success('Realisasi disimpan')
      onClose()
    },
    onError: (e: any) =>
      toast.error(e?.error?.message || 'Gagal menyimpan'),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!realisasi.trim()) { toast.error('Isi nilai realisasi terlebih dahulu'); return }
    mutation.mutate()
  }

  const target = row[qInfo.targetKey]

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Edit Realisasi {q}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{row.name}</p>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {target && (
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <p className="text-xs text-gray-500 mb-0.5">Target</p>
              <p className="text-sm font-medium text-gray-700">{target}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Realisasi <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={realisasi}
              onChange={(e) => setRealisasi(e.target.value)}
              placeholder="Nilai realisasi…"
              autoFocus
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">% Pencapaian</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={110}
                step={0.1}
                value={realisasiPct}
                onChange={(e) => setRealisasiPct(e.target.value)}
                placeholder="0"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-500 shrink-0">% (maks 110)</span>
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
            >
              {mutation.isPending ? 'Menyimpan…' : 'Simpan'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
            >
              Batal
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Delete confirmation modal ────────────────────────────────────────────────

interface DeleteModalProps {
  row: CompanyKPIRow
  year: number
  onClose: () => void
}

function DeleteModal({ row, year, onClose }: DeleteModalProps) {
  const qc = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => penilaianApi.deleteCompanyKPI(row.name, year),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company-kpis', year] })
      toast.success('KPI dihapus')
      onClose()
    },
    onError: (e: any) =>
      toast.error(e?.error?.message || 'Gagal menghapus KPI'),
  })

  const quarterCount = QUARTERS.filter(({ idKey }) => !!row[idKey]).length

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Hapus KPI Perusahaan?</h3>
            <p className="text-sm text-gray-500 mt-1">
              <span className="font-medium text-gray-700">{row.name}</span> akan dihapus
              dari {quarterCount} kuartal dan{' '}
              <span className="font-medium text-gray-700">{row.employee_count} karyawan</span>.
              Tindakan ini tidak dapat dibatalkan.
            </p>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
          >
            {mutation.isPending ? 'Menghapus…' : 'Ya, Hapus'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
          >
            Batal
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PenilaianKPIPage() {
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingRow, setEditingRow] = useState<CompanyKPIRow | null>(null)
  const [deletingRow, setDeletingRow] = useState<CompanyKPIRow | null>(null)
  const [editingRealisasi, setEditingRealisasi] = useState<{ row: CompanyKPIRow; q: string; qn: number } | null>(null)
  const qc = useQueryClient()

  const syncMutation = useMutation({
    mutationFn: () => penilaianApi.syncCompanyKPIAssignments(selectedYear),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['company-kpis', selectedYear] })
      toast.success(`Sinkronisasi selesai — ${res.created} assignment dibuat`)
    },
    onError: (e: any) =>
      toast.error(e?.error?.message || 'Gagal sinkronisasi'),
  })

  // ── periods ───────────────────────────────────────────────────────────────

  const { data: periodsData } = useQuery({
    queryKey: ['penilaian-periods'],
    queryFn: () => penilaianApi.getPeriods(),
  })

  const periods = periodsData?.data ?? []

  const availableYears = useMemo(() => {
    const ys = new Set(periods.map((p) => p.year))
    return Array.from(ys).sort((a, b) => b - a)
  }, [periods])

  // Sync selected year to the most recent available year once data loads
  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableYears])

  const yearPeriods: YearPeriods = useMemo(
    () => ({
      Q1: periods.find((p) => p.year === selectedYear && p.quarter === 1),
      Q2: periods.find((p) => p.year === selectedYear && p.quarter === 2),
      Q3: periods.find((p) => p.year === selectedYear && p.quarter === 3),
      Q4: periods.find((p) => p.year === selectedYear && p.quarter === 4),
    }),
    [periods, selectedYear]
  )

  const hasAnyPeriod = Object.values(yearPeriods).some(Boolean)

  // ── company KPI data ──────────────────────────────────────────────────────

  const { data: companyKPIs = [], isLoading } = useQuery({
    queryKey: ['company-kpis', selectedYear],
    queryFn: () => penilaianApi.getCompanyKPIs(selectedYear),
    enabled: hasAnyPeriod,
  })

  const yearSelectOptions = availableYears.length > 0 ? availableYears : [currentYear]

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">KPI Perusahaan</h1>
          <p className="text-sm text-gray-500 mt-1">
            KPI berlaku untuk semua karyawan — target berbeda per kuartal
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-medium">Tahun</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[90px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {yearSelectOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          {hasAnyPeriod && (
            <>
              <button
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending}
                title="Buat assignment yang belum ada untuk semua karyawan"
                className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {syncMutation.isPending ? 'Sinkronisasi…' : 'Sinkronkan'}
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                + Tambah KPI
              </button>
            </>
          )}
        </div>
      </div>

      {/* Quarter availability chips */}
      <div className="flex items-center gap-2 mb-6">
        {QUARTERS.map(({ q }) => {
          const p = yearPeriods[q]
          return (
            <span
              key={q}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                p ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-400'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${p ? 'bg-blue-500' : 'bg-gray-300'}`} />
              {q}{p ? ` · ${p.status}` : ' · —'}
            </span>
          )
        })}
      </div>

      {/* Content */}
      {!hasAnyPeriod ? (
        <div className="text-center py-24 text-gray-400 bg-white border border-dashed border-gray-300 rounded-xl">
          <p className="font-medium">Belum ada periode untuk tahun {selectedYear}</p>
          <p className="text-sm mt-1">
            Buat periode Q1–Q4 di halaman Manajemen Periode terlebih dahulu
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Daftar KPI Perusahaan {selectedYear}</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {companyKPIs.length} KPI terdaftar
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="py-16 text-center text-sm text-gray-400">Memuat…</div>
          ) : companyKPIs.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-400">
              <p>Belum ada KPI Perusahaan untuk tahun {selectedYear}</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                + Tambah KPI Pertama
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600">Nama KPI</th>
                    <th className="text-center px-3 py-2.5 font-medium text-gray-600 w-16">Bobot</th>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-600 w-36">Target Tahunan</th>
                    {QUARTERS.map(({ q }) => (
                      <th
                        key={q}
                        className={`text-left px-3 py-2.5 font-medium w-44 ${
                          yearPeriods[q] ? 'text-gray-600' : 'text-gray-300'
                        }`}
                      >
                        {q}
                        {yearPeriods[q] && (
                          <span className="ml-1 text-xs font-normal text-gray-400">
                            ({yearPeriods[q]!.status})
                          </span>
                        )}
                      </th>
                    ))}
                    <th className="text-center px-3 py-2.5 font-medium text-gray-600 w-24">
                      Karyawan
                    </th>
                    <th className="w-20" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {companyKPIs.map((row, i) => (
                    <tr key={`${row.name}-${i}`} className="hover:bg-gray-50/60 group">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{row.name}</div>
                        {row.target_description && row.target_description !== row.name && (
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                            {row.target_description}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center font-semibold text-gray-800">
                        {row.bobot}%
                      </td>
                      <td className="px-3 py-3">
                        {row.annual_target ? (
                          <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                            {row.annual_target}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300 italic">—</span>
                        )}
                      </td>
                      {QUARTERS.map(({ q, qn, targetKey, idKey, realisasiKey, realisasiPctKey }) => {
                        const hasPeriod = !!yearPeriods[q]
                        const target = row[targetKey]
                        const hasKpi = !!row[idKey]
                        const realisasi = row[realisasiKey]
                        const realisasiPct = row[realisasiPctKey]
                        return (
                          <td key={q} className="px-3 py-3 align-top">
                            {!hasPeriod ? (
                              <span className="text-gray-200 text-xs">—</span>
                            ) : !hasKpi ? (
                              <span className="text-xs text-amber-500 italic">Belum dibuat</span>
                            ) : (
                              <div className="space-y-1">
                                <div className="text-xs text-gray-500">
                                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Target: </span>
                                  {target ? <span className="text-gray-700">{target}</span> : <span className="italic text-gray-300">—</span>}
                                </div>
                                <div className="text-xs text-gray-500">
                                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Realisasi: </span>
                                  {realisasi ? <span className="text-gray-700">{realisasi}</span> : <span className="italic text-gray-300">—</span>}
                                </div>
                                {realisasiPct != null && (
                                  <span className={`inline-block text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                                    realisasiPct >= 100 ? 'bg-green-100 text-green-700' :
                                    realisasiPct >= 75  ? 'bg-yellow-100 text-yellow-700' :
                                                          'bg-red-100 text-red-700'
                                  }`}>
                                    {realisasiPct}%
                                  </span>
                                )}
                                <div>
                                  <button
                                    onClick={() => setEditingRealisasi({ row, q, qn })}
                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-0.5"
                                  >
                                    Edit Realisasi
                                  </button>
                                </div>
                              </div>
                            )}
                          </td>
                        )
                      })}
                      <td className="px-3 py-3 text-center">
                        <span className="text-sm font-medium text-gray-700">
                          {row.employee_count}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setEditingRow(row)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Edit KPI"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setDeletingRow(row)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Hapus KPI"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Info box */}
      {hasAnyPeriod && (
        <div className="mt-4 px-4 py-3 bg-blue-50 rounded-xl flex items-start gap-3">
          <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-blue-700">
            <span className="font-semibold">KPI Perusahaan</span> secara otomatis ditetapkan ke seluruh
            karyawan aktif. KPI Individu dikelola oleh masing-masing karyawan di halaman{' '}
            <span className="font-semibold">KPI Saya</span>.
          </p>
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <KPIFormModal
          year={selectedYear}
          yearPeriods={yearPeriods}
          editing={null}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {editingRow && (
        <KPIFormModal
          year={selectedYear}
          yearPeriods={yearPeriods}
          editing={editingRow}
          onClose={() => setEditingRow(null)}
        />
      )}

      {deletingRow && (
        <DeleteModal
          row={deletingRow}
          year={selectedYear}
          onClose={() => setDeletingRow(null)}
        />
      )}

      {editingRealisasi && (
        <RealisasiModal
          row={editingRealisasi.row}
          q={editingRealisasi.q}
          qn={editingRealisasi.qn}
          year={selectedYear}
          onClose={() => setEditingRealisasi(null)}
        />
      )}
    </div>
  )
}
